/**
 * useStationSettings - Organization-scoped station settings from database
 *
 * Fetches station toggle states from the prod_settings table:
 * - pretreatStationEnabled: Whether pretreat station is active
 * - packerPriorityEnabled: Whether packer priority system is active
 *
 * Unlike useProductionSettings (localStorage), this reflects org-wide settings
 * that affect all users in the organization.
 *
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic rollback on server errors
 * - Error callback for toast notifications
 * - Loading states for initial fetch and updates
 */
import { useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { skipToken } from '@tanstack/react-query';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';

export interface StationSettings {
  pretreatStationEnabled: boolean;
  packerPriorityEnabled: boolean;
  defaultSkipVerification: boolean;
  dtfEnabled: boolean;
}

const DEFAULT_SETTINGS: StationSettings = {
  pretreatStationEnabled: true,
  packerPriorityEnabled: true,
  defaultSkipVerification: false,
  dtfEnabled: false,
};

interface UpdateResult {
  success: boolean;
  error?: string;
}

/**
 * Hook to fetch and update station settings from the database.
 *
 * @returns Object containing:
 * - settings: Current station settings (defaults while loading)
 * - isLoading: True during initial data fetch
 * - isError: True if fetch failed
 * - updateSettings: Function to update settings (returns promise with result)
 * - isUpdating: True while an update is in progress
 */
export function useStationSettings(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { selectedLocation } = useLocationContext();
  const utils = trpc.useUtils();

  // Only fetch if we have a location (which means user is authenticated) and
  // the caller has not turned the read off (a role the procedure refuses).
  const query = trpc.productionSettings.getStationSettings.useQuery(
    enabled && selectedLocation?.id ? undefined : skipToken,
    {
      staleTime: 60_000, // Cache for 1 minute
      refetchOnWindowFocus: false,
    }
  );

  const updateMutation = trpc.productionSettings.updateStationSettings.useMutation({
    // Optimistic update for instant UI feedback
    onMutate: async (newSettings) => {
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await utils.productionSettings.getStationSettings.cancel();

      // Snapshot previous value for rollback
      const previousSettings = utils.productionSettings.getStationSettings.getData();

      // Optimistically update the cache
      if (previousSettings) {
        utils.productionSettings.getStationSettings.setData(undefined, {
          ...previousSettings,
          ...(newSettings.pretreatStationEnabled !== undefined && {
            pretreatStationEnabled: newSettings.pretreatStationEnabled,
          }),
          ...(newSettings.packerPriorityEnabled !== undefined && {
            packerPriorityEnabled: newSettings.packerPriorityEnabled,
          }),
          ...(newSettings.defaultSkipVerification !== undefined && {
            defaultSkipVerification: newSettings.defaultSkipVerification,
          }),
          ...(newSettings.dtfEnabled !== undefined && {
            dtfEnabled: newSettings.dtfEnabled,
          }),
        });
      }

      return { previousSettings };
    },
    // Rollback on error
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        utils.productionSettings.getStationSettings.setData(undefined, context.previousSettings);
      }
    },
    // Refetch after success to ensure consistency
    onSettled: () => {
      utils.productionSettings.getStationSettings.invalidate();
    },
  });

  /**
   * Update station settings with error handling.
   *
   * @param updates - Partial settings to update
   * @returns Promise resolving to result object with success/error
   */
  const updateSettings = useCallback(
    async (updates: Partial<StationSettings>): Promise<UpdateResult> => {
      try {
        await updateMutation.mutateAsync({
          pretreatStationEnabled: updates.pretreatStationEnabled,
          packerPriorityEnabled: updates.packerPriorityEnabled,
          defaultSkipVerification: updates.defaultSkipVerification,
          dtfEnabled: updates.dtfEnabled,
        });
        return { success: true };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to save setting. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [updateMutation]
  );

  return {
    /** Current settings (defaults while loading) */
    settings: query.data ?? DEFAULT_SETTINGS,
    /**
     * True once the server has actually answered — i.e. `settings` is this
     * org's real config rather than DEFAULT_SETTINGS.
     *
     * Needed because `isLoading` does NOT cover every unresolved state. This
     * query passes `skipToken` until LocationContext resolves a location, and
     * react-query reports a skipped query as `status: 'pending'` /
     * `fetchStatus: 'idle'` — and v5's `isLoading` is `isPending && isFetching`,
     * so a SKIPPED query has isLoading === false, isError === false and no
     * data. `!isLoading` therefore reads as "answered" while the query has in
     * fact never run, and consumers silently get DEFAULT_SETTINGS.
     *
     * Any consumer gating UI on a setting whose default is `false` must gate on
     * this flag, not on `!isLoading`: a `false` default is only meaningful as an
     * answer for a genuinely-new org, never as a stand-in for "we did not ask".
     */
    isResolved: query.isSuccess,
    /** True during initial data fetch. NOTE: false for a skipped query — see isResolved. */
    isLoading: query.isLoading,
    /** True if fetch failed */
    isError: query.isError,
    /** Update settings function - returns promise with result */
    updateSettings,
    /** True while an update is in progress */
    isUpdating: updateMutation.isPending,
    /** Last mutation error (if any) */
    lastError: updateMutation.error?.message,
  };
}
